/**
 * LLM 适配器（可选，默认关闭）
 * ------------------------------------------------------------
 * 用途：当本地规则识别不到「音标 / 中文意思 / 例句」时，可选调用大模型补全。
 *
 * 三条硬约束（来自需求）：
 *   1. 默认关闭，必须在设置页填 API Key 才启用；不填就完全走本地规则
 *   2. Prompt 强制要求「原文没有的返回 null」，不允许模型凭空补全
 *   3. 输出只写进草稿（imports 集合），仍需人工逐条确认才能入库
 *
 * 兼容 OpenAI 的 /chat/completions 协议（OpenAI / DeepSeek / 通义 / Moonshot /
 * 本地 Ollama 等都支持），只需改 baseUrl 与 model。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';

const KEY_ROW = 'llmConfig';

export const DEFAULT_LLM_CONFIG = {
  enabled: false,
  baseUrl: 'https://api.openai.com/v1',
  model: 'gpt-4o-mini',
  apiKey: '',
  temperature: 0,
  maxBatch: 30,
};

export async function loadLLMConfig() {
  const row = await getStorage().get(COLLECTIONS.META, KEY_ROW);
  return { ...DEFAULT_LLM_CONFIG, ...(row?.value || {}) };
}

export async function saveLLMConfig(patch) {
  const next = { ...(await loadLLMConfig()), ...patch };
  await getStorage().put(COLLECTIONS.META, { key: KEY_ROW, value: next });
  return next;
}

export async function isLLMEnabled() {
  const cfg = await loadLLMConfig();
  return !!(cfg.enabled && cfg.apiKey);
}

const SYSTEM_PROMPT = `你是小学英语教材数据助手。你的任务是从给定的原始文本中抽取结构化数据。
严格遵守：
1. 只输出 JSON，不要任何解释文字或 Markdown 代码块标记。
2. 原文中没有的信息一律填 null，绝对不要猜测、不要自行编造中文意思、音标或例句。
3. 无法确定的内容不要输出该字段，而不是输出空字符串。
4. 输出格式：
{"words":[{"en":"...","zh":"...","phonetic":null,"pos":null}],"sentences":[{"en":"...","zh":"..."}]}`;

function buildUserPrompt(rawText, need = 'words') {
  return `请从下面这段教材原文中抽取${need === 'words' ? '单词表' : '句子表'}。
原文：
"""
${rawText}
"""`;
}

/** 调用一次 /chat/completions，返回解析后的 JSON 或 null */
export async function completeJSON(rawText, { need = 'words', cfg } = {}) {
  const conf = cfg || await loadLLMConfig();
  if (!conf.enabled || !conf.apiKey) {
    throw new Error('LLM 未启用：请在「设置 → AI 补全」中填写 API Key');
  }
  const url = `${conf.baseUrl.replace(/\/$/, '')}/chat/completions`;
  const body = {
    model: conf.model,
    temperature: conf.temperature ?? 0,
    response_format: { type: 'json_object' },
    messages: [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: buildUserPrompt(rawText, need) },
    ],
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${conf.apiKey}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`LLM 请求失败 ${res.status}：${text.slice(0, 200)}`);
  }
  const data = await res.json();
  const content = data?.choices?.[0]?.message?.content;
  if (!content) return null;
  try {
    return JSON.parse(content);
  } catch {
    // 容错：模型偶尔会包一层 ```json
    const m = content.match(/\{[\s\S]*\}/);
    return m ? JSON.parse(m[0]) : null;
  }
}

/**
 * 用 LLM 补全草稿中缺失的字段（只填空缺，不覆盖本地识别结果）
 * @param {object} draft  extractor 产出的草稿
 * @returns {Promise<{draft:object, changed:number}>}
 */
export async function enrichDraft(draft, cfg) {
  const conf = cfg || await loadLLMConfig();
  if (!conf.enabled || !conf.apiKey) return { draft, changed: 0 };

  const missing = draft.words.filter(w => !w.zh || !w.phonetic);
  if (!missing.length) return { draft, changed: 0 };

  const rawText = missing.map(w => `${w.en}${w.phonetic ? ' ' + w.phonetic : ''}`).join('\n');
  const result = await completeJSON(rawText, { need: 'words', cfg: conf });
  if (!result?.words) return { draft, changed: 0 };

  const map = new Map(result.words.map(w => [String(w.en || '').toLowerCase().trim(), w]));
  let changed = 0;
  const words = draft.words.map(w => {
    const hit = map.get(w.en.toLowerCase().trim());
    if (!hit) return w;
    const next = { ...w };
    // 只填补空字段；仍保留 needsReview，人工必须核对
    if (!next.zh && hit.zh) { next.zh = String(hit.zh); changed++; }
    if (!next.phonetic && hit.phonetic) { next.phonetic = String(hit.phonetic); changed++; }
    if (!next.pos && hit.pos) { next.pos = String(hit.pos); changed++; }
    if (changed) next.reviewNote = 'AI 补全过部分字段，请核对';
    return next;
  });

  return { draft: { ...draft, words }, changed };
}
